"use client";

import { useTranslations } from "next-intl";
import { useId, useMemo, useState } from "react";
import { CustomScroll } from "@/components/CustomScroll";
import { useProjectConfigs } from "@/features/project/ProjectContext";
import { useProjectText } from "@/features/project/useProjectText";
import { cn } from "@/lib/cn";
import type { FilterWithFacet, ValueFacet } from "../useFacets";
import { useSelectedFilters } from "../useSelectedFilters";

/** Above this many values, the list gets its own search box. */
const SEARCHABLE_FROM = 5;

/**
 * A checkbox list of a field's values.
 *
 * Every value the field takes is listed, not just the ones still reachable:
 * counts come from the filtered query, so a value that would now yield nothing
 * renders disabled rather than disappearing — a list that reshuffles as you tick
 * boxes is impossible to use, and a value that vanished when selected could
 * never be unselected.
 */
export function ValueFilter({
  filter,
  label,
  onToggled,
}: {
  filter: FilterWithFacet;
  /** The filter's resolved label, so its reset button names itself. */
  label: string;
  onToggled: () => void;
}) {
  const t = useTranslations();
  const projectText = useProjectText();
  const configs = useProjectConfigs();
  const { selectedFilters, toggle, reset } = useSelectedFilters();
  const [search, setSearch] = useState("");
  const fieldId = useId();

  const { def } = filter;
  const selected = useMemo(
    () => new Set(selectedFilters[def.name] ?? []),
    [selectedFilters, def.name]
  );

  const all =
    filter.all?.type === "values" ? (filter.all as ValueFacet) : undefined;
  const available =
    filter.facet?.type === "values" ? (filter.facet as ValueFacet) : undefined;

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const entry of available?.values ?? [])
      map.set(entry.value, entry.count);
    return map;
  }, [available]);

  const values = useMemo(() => {
    const mask = search.toLowerCase();
    const matching = (all?.values ?? []).filter((entry) =>
      entry.value.toLowerCase().includes(mask)
    );

    if (!configs.groupSelectedFilterItems) return matching;

    // Selected first, then still-reachable, then the rest — so what the user
    // chose stays visible without scrolling once a long list is filtered down.
    const rank = (value: string) =>
      selected.has(value) ? 0 : counts.has(value) ? 1 : 2;
    return [...matching].sort((a, b) => rank(a.value) - rank(b.value));
  }, [all, search, configs.groupSelectedFilterItems, selected, counts]);

  return (
    <div className="tablet:max-h-64 rounded-lg">
      <CustomScroll className="tablet:max-h-64 rounded-lg">
        <div className="py-1">
          {(all?.values.length ?? 0) > SEARCHABLE_FROM && (
            <div className="px-4 py-2 hidden tablet:block">
              <input
                type="text"
                className={cn(
                  "w-full rounded-lg tablet:border border-gray-300 bg-white px-2 py-1 text-sm",
                  "dark:border-gray-600 dark:bg-gray-800 dark:text-off-white"
                )}
                placeholder={t("search-input.placeholder")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                aria-label={t("search-input.placeholder")}
              />
            </div>
          )}

          {values.map((entry) => {
            const isSelected = selected.has(entry.value);
            const count = counts.get(entry.value) ?? 0;
            // Nothing would come back, and it isn't already on — so there is
            // nothing for a click to do.
            const isDisabled = !counts.has(entry.value) && !isSelected;

            const inputId = `${fieldId}-${entry.value}`;

            return (
              // The control and its label are siblings joined by `htmlFor`,
              // rather than the label wrapping the input: nesting makes a click
              // on the control ambiguous — the label forwards it to the very
              // control that was clicked — and browsers only paper over that by
              // convention.
              <div
                key={entry.value}
                className={cn(
                  "group flex items-center justify-between px-4 py-1.5",
                  "tablet:hover:bg-primary tablet:hover:dynamic-text",
                  isDisabled && "opacity-50",
                  !isSelected && "text-gray-800 dark:text-off-white"
                )}
              >
                <span className="flex items-center">
                  <input
                    id={inputId}
                    type="checkbox"
                    className="h-4 w-4 rounded tablet:border-gray-300 text-primary focus:ring-primary"
                    checked={isSelected}
                    disabled={isDisabled}
                    onChange={() => {
                      toggle(def.name, entry.value);
                      onToggled();
                    }}
                  />
                  <label htmlFor={inputId} className={cn("ml-2 text-sm")}>
                    {projectText(entry.value, entry.value)}
                  </label>
                </span>
                <span className="rounded-full bg-primary-opacity-25 px-2 py-0.5 text-xs font-medium group-hover:bg-black-opacity-10 group-hover:dynamic-text">
                  {count}
                </span>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => reset(def.name)}
          aria-label={`${t("search.filters.clear")}: ${label}`}
          className="w-full cursor-pointer px-4 py-2 text-left font-semibold hover:text-primary hidden tablet:inline-flex"
        >
          {t("search.filters.clear")}
        </button>
      </CustomScroll>
    </div>
  );
}
